import React, { useContext } from 'react';
import { BasketType } from '../../../../models/basket-type';
import guestUpdateLineItem from '../../../../services/content-api/endpoints/basket/guest/update-line-item';
import memberUpdateLineItem from '../../../../services/content-api/endpoints/basket/member/update-line-item';
import useGetSessionId from '../../user/hooks/use-get-session-id.hook';
import { UserStateContext } from '../../user/user.provider';
import basketLoading from '../actions/basket-loading';
import updateBasket from '../actions/update-basket';
import { BasketDispatchContext, BasketStateContext } from '../basket.provider';
import GtmEventNameEnum from '../../../../models/gtm-event-name-enum';
import { BasketClearQuoteLabelsType } from '../../../../models/basket-clear-quote-labels-type';
import createAsyncAlertModal from '../../../../helpers/create-async-alert-modal';
import { OverlayDispatchContext } from '../../overlay/overlay.provider';
import useDeselectQuote from './use-deselect-quote';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../data-layer/data-layer.provider';
import open from '../../overlay/open.overlay.actions';
import AddToBasketErrorsModal from '../../../molecules/add-to-basket-errors-modal/add-to-basket-errors-modal';

const useUpdateBasketLineItem: (
  clearQuoteLabels: BasketClearQuoteLabelsType,
  addProductErrorHeading: string,
  addProductErrorBody: string
) => (lineItemId: number, quantity: number) => Promise<BasketType> = (
  clearQuoteLabels,
  addProductErrorHeading,
  addProductErrorBody
) => {
  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);
  const { auth } = useContext(UserStateContext);
  const basketState = useContext(BasketStateContext);
  const basketDispatch = useContext(BasketDispatchContext);
  const overlayDispatch = useContext(OverlayDispatchContext);
  const getSessionId = useGetSessionId();
  const deselectQuote = useDeselectQuote();

  return async (lineItemId, quantity) => {
    basketDispatch(basketLoading(true));

    try {
      if (basketState.quote) {
        await createAsyncAlertModal(
          overlayDispatch,
          clearQuoteLabels.clearQuoteHeading,
          clearQuoteLabels.clearQuoteDescription,
          clearQuoteLabels.clearQuoteAccept,
          clearQuoteLabels.clearQuoteCancel,
          clearQuoteLabels.clearQuoteHelp
        );

        await deselectQuote();
      }
    } catch (error) {
      basketDispatch(basketLoading(false));

      throw error;
    }

    let basket: BasketType;
    try {
      basket = auth.accessToken
        ? await memberUpdateLineItem(
            quantity,
            lineItemId,
            await getSessionId(),
            basketState.id
          )
        : await guestUpdateLineItem(
            quantity,
            lineItemId,
            await getSessionId(),
            basketState.id
          );
    } catch (error) {
      basketDispatch(basketLoading(false));
      throw error;
    }

    basketDispatch(updateBasket(basket));
    basketDispatch(basketLoading(false));

    if (basket?.lineItemIssues?.length) {
      const skus = basket.lineItemIssues.map((item) => item.variantSku);
      const issue = basket.lineItemIssues[0];

      overlayDispatch(
        open(
          <AddToBasketErrorsModal
            heading={addProductErrorHeading}
            description={issue?.errorMessage}
            body={addProductErrorBody}
            skus={skus}
          />
        )
      );
    }

    pushToDataLayer(GtmEventNameEnum.ProductUpdate, {
      ecommerce: {
        detail: {
          products: basket.lineItems.map((lineItem) => {
            return {
              price: lineItem.buyPriceFormatted,
              productImage: lineItem.product.properties.webImageLarge1425,
              productName: lineItem.product.name,
              productCode: lineItem.product.variantSku,
              masterCode: lineItem.product.sku,
              listPrice: lineItem.listPriceFormatted,
              quantity: lineItem.quantitySatisfied,
            };
          }),
        },
      },
      basketValue: basket.formattedTotal,
    });

    return basket;
  };
};

export default useUpdateBasketLineItem;
